import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

/**Components**/
import { stringToArray } from "cron-converter";
import parser from "cron-parser";
import Cron from "../../utils/components/common/cron/Cron";
import "react-js-cron/dist/styles.css";
import Modal from "../../utils/components/common/modal";

/**Utils**/
import { UserContext } from "../../layouts/MainLayout";
const defaultPeriod = "week";

const AddJob = ({ onAddJobModalClose, existingJobs, jobToEdit }) => {
  const { t } = useTranslation("schedules");
  const { featureEnabledStatus } = useContext(UserContext);
  const [cronValue, setCronValue] = useState(jobToEdit?.jobTime);
  const [isCronValid, setIsCronValid] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(true);
  const [mode, setMode] = useState(jobToEdit?.targetMode || "OPERATIONAL");
  const [err, setErr] = useState(false);

  useEffect(() => {
    /**In case of edit job,
     * we remove that particular job from existing job list for 30 mins cron validation check.
     */
    if (jobToEdit?.index || jobToEdit?.index === 0) {
      delete existingJobs[jobToEdit.index];
    }
  }, [existingJobs, jobToEdit]);

  const dropdownConfig = useMemo(
    () => ({
      period: {
        disabled: true,
      },
      "week-days": {
        allowClear: false,
      },
      hours: {
        mode: "single",
        allowClear: false,
      },
      minutes: {
        mode: "single",
        leadingZero: true,
        allowClear: false,
      },
    }),
    []
  );

  const onCancelClick = useCallback(() => {
    setIsModalOpen(false);
    onAddJobModalClose(null);
  }, [onAddJobModalClose]);

  const onSaveClick = useCallback(() => {
    setIsModalOpen(false);
    onAddJobModalClose({
      jobTime: cronValue,
      targetMode: mode,
      addEditType: jobToEdit ? "EDIT" : "ADD",
      index: jobToEdit?.index,
    });
  }, [cronValue, mode, onAddJobModalClose, jobToEdit]);

  const handleModeChange = useCallback((e) => {
    setMode(e.target.value);
  }, []);

  const updateCronValue = useCallback((value) => {
    setCronValue(value);

    /* Cron format   "x x * * x" || "x x * * *"
     * No * on minutes or seconds place
     */
    const cronArr = value.split(" ");
    if (cronArr[0] === "*" || cronArr[1] === "*") {
      setIsCronValid(false);
      return;
    }
    setIsCronValid(true);
  }, []);

  const validateCronTimeDiff = useCallback(
    (currentCronExp, existingCronExpList) => {
      let isValid = true;

      if (defaultPeriod === "week") {
        let existingCronList = [];
        let currentCronList = [];

        /**For weekly, need to break cronExpression of all the job in diff cron expression to check 30 mins diff, as cron can have multiple days**/
        existingCronExpList.forEach((cronExp) => {
          const parts = cronExp.split(" ");
          const daysArr = stringToArray(cronExp)[4];
          daysArr.forEach((day) => {
            const exp = `${parts[0]} ${parts[1]} ${parts[2]} ${parts[3]} ${day}`;
            existingCronList.push(exp);
          });
        });

        /**For weekly, need to break current cronExpression, as cron can have multiple days**/
        const parts = currentCronExp.split(" ");
        const daysArr = stringToArray(currentCronExp)[4];
        daysArr.forEach((day) => {
          const exp = `${parts[0]} ${parts[1]} ${parts[2]} ${parts[3]} ${day}`;
          currentCronList.push(exp);
        });

        /**Compare cron expression of devided cron of each job with each divided cron of currentCronExpression**/
        existingCronList.forEach((jobs) => {
          const interval = parser.parseExpression(jobs);
          const nextTime = interval.next().getTime();

          currentCronList.forEach((jobs) => {
            const interval1 = parser.parseExpression(jobs);
            const nextTime1 = interval1.next().getTime();
            const timediff = Math.abs(nextTime - nextTime1) / 60000;
            if (timediff < 30) isValid = false;
          });
        });
      }
      setErr(!isValid);
      return isValid;
    },
    []
  );

  const getCronExpression = useCallback((cronExp) => {
    if(!cronExp) return undefined;
    const cronDays = stringToArray(cronExp)[4];
    const splitedCron = cronExp.split(" ");
    splitedCron[4] = cronDays.join(",");
    const combinedCron = splitedCron.join(" ");
    return combinedCron;
  }, []);

  const isValid = useMemo(() => {
    if (!isCronValid) return false;
    // If cron is valid and there is no existing job to validate 30 mins check then return true.
    if (!existingJobs.length) return true;

    const cronJobsList = existingJobs.map((jobs) => jobs.jobTime);

    let isValid = validateCronTimeDiff(cronValue, cronJobsList);
    /**In case of edit job/task, if there is not change in data then disable the save btn */
    if (
      getCronExpression(jobToEdit?.jobTime) === getCronExpression(cronValue) &&
      mode === jobToEdit?.targetMode
    ) {
      isValid = false;
    }
    return isValid;
  }, [
    isCronValid,
    existingJobs,
    validateCronTimeDiff,
    cronValue,
    getCronExpression,
    jobToEdit?.jobTime,
    jobToEdit?.targetMode,
    mode,
  ]);

  return (
    <div>
      <Modal isOpen={isModalOpen} className="add-job-modal" idName={jobToEdit ? "edit-task-modal": "add-task-modal"}>
        {/* Header Starts */}
        <div className="job-header">
          <p className="heading heading-h1">{jobToEdit ? t("editTask") : t("addTask")}</p>
        </div>
        {/* Header Ends */}
        {/* Body Container Starts */}
        <div className="job-body-container">
          <div className="cron-section-wrapper">
          <p className="title-1 trigger heading-h3" id="schd-trigger">{t("scheduleTrigger")}</p>
          <div>
            <Cron
              value={cronValue}
              setValue={(v) => {
                updateCronValue(v);
              }}
              clockFormat="12-hour-clock"
              defaultPeriod={defaultPeriod}
              dropdownsConfig={dropdownConfig}
              className="schd-cron" // don't change this class name. It's used to create cron-class internally
              locale={{
                emptyWeekDays: t("cronPlaceholder.everyDayOfWeek"),
                emptyMinutes: t("cronPlaceholder.minute"),
                emptyHours: t("cronPlaceholder.hour"),
              }}
              periodicityOnDoubleClick={false}
              // allowedPeriods={["week"]}
            />
          </div>
          {isCronValid && err && (
            <p className="error min30Err text-h4">{t("min30Err")}</p>
          )}
          </div>

          <div className="mode-section-wrapper">
          <div className="mode-section">
            <p className="title-1 changemode heading-h3" id="change-mode-title">{t("changeModeTo")}</p>

            <div className="radio-container">
              <label
                id="mode-operational"
                className={
                  mode === "OPERATIONAL"
                    ? "active-radio radio-button operational_label text-h1"
                    : "non-active radio-button operational_label text-h1"
                }
              >
                <input
                  type="radio"
                  name="OPERATIONAL"
                  value="OPERATIONAL"
                  checked={mode === "OPERATIONAL"}
                  className={
                    mode === "OPERATIONAL"
                      ? "active operational_option"
                      : "operational_option"
                  }
                  onChange={handleModeChange}
                />
                {t("modes.operational")}
              </label>
              <label
                id="mode-sense-only"
                className={
                  mode === "SENSE_ONLY"
                    ? "active-radio radio-button senseonly_label text-h1"
                    : "non-active radio-button senseonly_label text-h1"
                }
              >
                <input
                  type="radio"
                  className={
                    mode === "SENSE_ONLY"
                      ? "active senseonly_option"
                      : "senseonly_option"
                  }
                  name="SENSE_ONLY"
                  value="SENSE_ONLY"
                  checked={mode === "SENSE_ONLY"}
                  onChange={handleModeChange}
                />
                {t("modes.senseOnly")}
              </label>
              <label
                id="mode-standby"
                className={
                  mode === "STANDBY"
                    ? "active-radio radio-button standby_label text-h1"
                    : "non-active radio-button standby_label text-h1"
                }
              >
                <input
                  type="radio"
                  className={
                    mode === "STANDBY"
                      ? "active standby_option"
                      : "standby_option"
                  }
                  name="STANDBY"
                  value="STANDBY"
                  checked={mode === "STANDBY"}
                  onChange={handleModeChange}
                />
                {t("modes.standBy")}
              </label>
              {featureEnabledStatus.researchMode && (
                <label
                  id="mode-research"
                  className={
                    mode === "RESEARCH"
                      ? "active-radio radio-button research_label text-h1"
                      : "non-active radio-button research_label text-h1"
                  }
                >
                  <input
                    type="radio"
                    className={
                      mode === "RESEARCH"
                        ? "active research_option"
                        : "research_option"
                    }
                    name="RESEARCH"
                    value="RESEARCH"
                    checked={mode === "RESEARCH"}
                    onChange={handleModeChange}
                  />
                  {t("modes.research")}
                </label>
              )}
            </div>
          </div>
          </div>
        </div>

        <div className="job-footer">
          <button id="cancel" className="roundBtn dark" onClick={onCancelClick}>
            <span>{t("btn.cancel")}</span>
          </button>
          <button
            id="save"
            className="roundBtn primary"
            onClick={onSaveClick}
            disabled={!isValid}
          >
            <span>{t("btn.save")}</span>
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default AddJob;
